import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Groups } from '../../api/Groups';
import { Users } from '../../api/Users';
import AppLoader from '../../components/AppLoader';
import AppSearch from '../../components/AppSearch';
import IconButton from '../../components/IconButton';
import UserItem from '../../components/UserItem';
import { useAuth } from '../../contexts/auth';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { GroupModel } from '../../models/Group';
import { User, UserRole } from '../../models/User';
import { Patients } from '../../api/Patients';
import { Doctors } from '../../api/Doctors';

export default function PatientsList() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [patients, setPatients] = useState<Partial<User[]>>();
  const [doctors, setDoctors] = useState<User[] | null>();
  const [userToDelete, setUserToDelete] = useState<User>();
  const [searchText, setSearchText] = useState<string>("");
  const [groupFilter, setGroupFilter] = useState<number | undefined>();
  const [groups, setGroups] = useState<GroupModel[]>();
  const [patient, setPatient] = useState<Partial<User>>();
  const [removeDoctor, setRemoveDoctor] = useState<boolean>(false);

  const { loggedUser } = useAuth();

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showAssignDoctorModal, setShowAssignDoctorModal] = useState<boolean>(false);

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();

  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [userPassChange, setUserPassChange] = useState<{ id: number, pwd: string }>({ id: 0, pwd: '' })


  useEffect(() => {
    if (loggedUser) {
      initPatients();
      initDoctors();
      initGroups();
    }
    // eslint-disable-next-line
  }, [loggedUser]);

  const handlePasswordModalClose = () => {
    setShowPasswordModal(false)
    setUserPassChange({ id: 0, pwd: '' })
  }

  const openChangePasswordModal = (user: User) => () => {
    setShowPasswordModal(true);
    setUserPassChange((prev) => ({ ...prev, id: user.id! }))
  }

  const createPatient = () => navigate('/patients/register');
  const editPatients = (id: number) => () => navigate(`/patients/${id}/edit`);
  const handleDeleteModalClose = () => setShowDeleteModal(false);

  const openDeleteModal = (user: User) => () => {
    setShowDeleteModal(true);
    setUserToDelete(user);
  }

  const handleAssignDoctorModalClose = () => setShowAssignDoctorModal(false);

  const openAssignDoctorModal = (user: User) => () => {
    setShowAssignDoctorModal(true);
    //user.refDoctor = { id: 0, name: "" }
    setPatient(user);
  }

  const manageUserCalendar = (id: number) => () => navigate(`/patients/${id}/calendar`);

  const initGroups = () => {
    Groups.getGroups()
      .then(
        ({ data: groups }) => {
          if (loggedUser!.role === UserRole.DOCTOR) setGroups(groups.filter(group => loggedUser!.groups!.map((g) => g.id).includes(group.id!)));
          else setGroups(groups);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('groups-error', message, `Groups Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handleFilterChange = (filter: 'search' | 'group') => (evt: React.ChangeEvent<any>) => {
    const value = evt.target.value;

    switch (filter) {
      case 'group': setGroupFilter(Number(value)); return;
      case 'search': default: setSearchText(value); return;
    }
  }

  const initPatients = () => {
    Patients.getPatients()
      .then(
        ({ data: patients }) => setPatients(patients.results),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('login-error', message, `Login Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const initDoctors = () => {
    Doctors.getDoctors()
      .then(
        ({ data: doctors }) => setDoctors(doctors.results),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('login-error', message, `Login Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const reset = () => {
    setPatients([]);
    initPatients();
  }

  const handlePassFieldChange = (evt: any) => {
    const value = evt.target.value;
    setUserPassChange((prev) => ({ ...prev, pwd: value }))
  }

  const changePassword = () => {
    const userDto = { id: userPassChange.id, password: userPassChange.pwd }
    showLoader!();
    Patients.updatePatient(userDto as User).then(() => {
      hideLoader!();
      handlePasswordModalClose()
      showToast!('patient-success', `User passowrd changed successfully`, 'Success', 'success');
      navigate('/patients');
    },
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        handlePasswordModalClose()
        hideLoader!();
        showToast!('patients-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
      })

  }

  const handleDoctorChange = () => (evt: React.ChangeEvent<any>) => {
    const value: number = evt.target.value;

    if (value)
      setRemoveDoctor(false)
    else
      setRemoveDoctor(true)

    setPatient({
      ...patient,
      refDoctor: {
        id: value,
        name: "name"
      },
    })
  }

  const assignToDoctor = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.stopPropagation();
    evt.preventDefault();
    (removeDoctor ? Patients.deAssignDoctor : Patients.assignDoctor)(patient?.id!, patient?.refDoctor?.id!)
      .then(
        () => {
          hideLoader!();
          showToast!('patient-success', 'User updated successfully', removeDoctor ? 'Doctor removed' : 'Doctor assigned', 'success');
          setPatients(patients?.map(p => { if (p?.id == patient?.id) return patient; return p }) as User[])
          handleAssignDoctorModalClose();
          //reset();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('patient-error', message, `Users Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const deleteUser = () => {
    showLoader!();

    Patients.deletePatient(userToDelete!.id!)
      .then(
        () => {
          hideLoader!();
          showToast!('patient-success', 'User deleted successfully', 'Delete Success', 'success');
          handleDeleteModalClose();
          reset();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('patient-error', message, `Users Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }
  const renderUsers = () => {
    const filteredUsers = patients!
      .filter(user => `${user?.firstname} ${user?.lastname}`.toLowerCase().includes(searchText.toLowerCase()))
      .filter(user => (groupFilter === 0 || groupFilter === undefined) || user?.groups?.map((g) => g.id).includes(groupFilter));

    return (
      <>
        <Row className="gx-2 mb-3">
          <Col md={6}>
            <AppSearch value={searchText} onChange={handleFilterChange('search')} placeholder={t('patientsList.search')} />
          </Col>
          <Col md={6}>
            <Form.Select size="sm" value={groupFilter} onChange={handleFilterChange('group')}>
              <option value="0">{t('usersList.allGroups')}</option>
              {groups?.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {filteredUsers.length === 0 && (
          <Alert className="py-2 mb-0">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">{t('patientsList.noUsers')}</span>
          </Alert>
        )}

        <ListGroup className="app-list">
          {filteredUsers.map(user => (
            <UserItem
              key={user?.id}
              user={user || new User()}
              trailing={
                <>
                  {user?.role !== "admin" && user?.groups!?.length > 0 && (
                    user?.groups!.map((group: any, index) => (
                      <Badge className="ms-2" bg="secondary" key={"gBadge-" + index}>
                        <FontAwesomeIcon icon={solid('users')} />
                        <span className="ms-2">{group.name}</span>
                      </Badge>))

                  )}
                  {user?.role !== "admin" && user?.groups!?.length === 0 && (
                    <Badge className="ms-2" bg="warning">
                      <FontAwesomeIcon icon={solid('question-circle')} />
                      <span className="ms-2">{t('usersList.noGroup')}</span>
                    </Badge>
                  )}
                </>
              }
              actions={(
                <>
                  {user?.groups &&
                    <IconButton className="ms-2" onClick={openAssignDoctorModal(user!)}>
                      <FontAwesomeIcon icon={solid('user-md')} />
                    </IconButton>
                  }
                  {loggedUser?.role === UserRole.DOCTOR &&
                    < IconButton className="ms-2" onClick={manageUserCalendar(user?.id!)}>
                      <FontAwesomeIcon icon={solid('calendar-alt')} />
                    </IconButton>
                  }


                  <IconButton onClick={editPatients(user?.id!)}>
                    <FontAwesomeIcon className="ms-2" icon={solid('pen')} />
                  </IconButton>
                  {loggedUser?.role === UserRole.ADMIN &&
                    <IconButton className="ms-2" onClick={openChangePasswordModal(user!)}>
                      <FontAwesomeIcon icon={solid('key')} />
                    </IconButton>
                  }
                  <IconButton className="ms-2 text-danger" onClick={openDeleteModal(user!)}>
                    <FontAwesomeIcon icon={solid('trash')} />
                  </IconButton>

                </>
              )}
            />
          ))}
        </ListGroup>
      </>
    )
  }

  const doctorList = () => {
    return doctors?.filter(doctor => doctor?.role! == "doctor" && doctor?.groups?.some(group => patient?.groups?.filter(group2 => group2.id == group.id)![0]))
      .map(doctor => <option key={doctor.id} value={doctor.id}>{doctor?.firstname + doctor?.lastname}</option>
      )
  }

  const renderAsyncContent = () => {
    return patients
      ? renderUsers()
      : (
        <div className="d-flex justify-content-center py-5">
          <AppLoader size={150} />
        </div>
      )
  }

  return (
    <Container>
      <Card>
        <div className="d-flex align-items-center justify-content-between border-bottom py-2 px-3">
          <Card.Title className="mb-0">{t('patientsList.title')}</Card.Title>

          <Button variant="success" size="sm" onClick={createPatient}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">{t('patientsList.create')}</span>
          </Button>
        </div>

        <Card.Body>{renderAsyncContent()}</Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.deleteTitle', { item: t('common.user') })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('patientsList.deleteMessage')} <b>{userToDelete?.firstname} {userToDelete?.lastname}</b>?

          <h5 className="my-4 text-center text-danger">{t('common.deleteWarning')}</h5>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" className="border" size="sm" onClick={handleDeleteModalClose}>{t('common.cancel')}</Button>
          <Button variant="danger" size="sm" onClick={deleteUser}>{t('common.delete')}</Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showAssignDoctorModal} onHide={handleAssignDoctorModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('patientEdit.assignDoctor')}</Modal.Title>
        </Modal.Header>

        <Form onSubmit={assignToDoctor}>
          <Modal.Body>

            <Form.Group controlId="patient-doctor" className="mb-3">
              <Form.Label>{t('patientEdit.doctor')}</Form.Label>
              <Form.Select size="sm" value={patient?.refDoctor?.id} onChange={handleDoctorChange()}>
                <option value="">{t('patientEdit.setDoctor')}</option>
                {doctorList()}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('patientEdit.doctor') })}</Form.Control.Feedback>
            </Form.Group>

          </Modal.Body>

          <Modal.Footer>
            <Button variant="light" className="border" size="sm" onClick={handleAssignDoctorModalClose}>{t('common.cancel')}</Button>
            <Button variant="success" size="sm" type="submit">{t('common.confirm')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>


      <Modal show={showPasswordModal} onHide={handlePasswordModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.changePassword')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Col md={12}>
            <Form.Group controlId="user-password" className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control size="sm" placeholder={t('doctorEdit.placeholders.password')} value={userPassChange.pwd} onChange={handlePassFieldChange} type="text" isInvalid={(userPassChange.pwd.length > 0 && userPassChange.pwd.length < 8) ? true : false} />
              <Form.Control.Feedback type="invalid">{t('errors.password8Char', { field: "password" })}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" className="border" size="sm" onClick={handlePasswordModalClose}>{t('common.cancel')}</Button>
          <Button variant="success" size="sm" onClick={changePassword} disabled={userPassChange.pwd.length < 8 ? true : false}>{t('common.confirm')}</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  )
}